import { trackGA4EventAfterPageView } from '@gumtree/shared/src/util/ga4-shared';
import { grayCalc, isInHost } from './grayCalc';

/**
 * This class is used to track images on the page and report them to the server.
 */
class ImageTracker {
    loadedImages: Set<string>;
    failedImages: Set<string>;
    observer: MutationObserver | null;
    intersectionObserver: IntersectionObserver | null;
    trackedElements: WeakSet<HTMLElement>;
    pendingCheckElements: Set<HTMLElement>;
    rafId: number | null;

    constructor() {
        this.loadedImages = new Set();
        this.failedImages = new Set();
        this.observer = null;
        this.intersectionObserver = null;
        this.trackedElements = new WeakSet();
        this.pendingCheckElements = new Set();
        this.rafId = null;
        this.init();
    }

    init() {
        // Defer initialization to avoid blocking page load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.startTracking());
        } else {
            // Use requestIdleCallback to defer execution and avoid blocking first paint
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => this.startTracking(), { timeout: 2000 });
            } else {
                setTimeout(() => this.startTracking(), 0);
            }
        }
    }

    startTracking() {
        this.trackExistingImages();
        this.observeDOMChanges();
        this.setupIntersectionObserver();
    }

    trackExistingImages() {
        // Track existing <img> tags only; background images checked on-demand via IntersectionObserver
        document.querySelectorAll('img').forEach((img) => {
            this.trackImage(img);
        });
    }

    /**
     * Check if element has background-image and track it
     * Optimized: only call getComputedStyle when necessary
     */
    checkAndTrackBackgroundImage(element: HTMLElement) {
        if (this.trackedElements.has(element) || !element.isConnected) {
            return;
        }

        // Check inline styles first to avoid expensive getComputedStyle call
        const inlineStyle = element.style.backgroundImage;
        if (inlineStyle && inlineStyle !== 'none') {
            const urlMatch = inlineStyle.match(/url\(["']?([^"']+)["']?\)/);
            if (urlMatch && urlMatch[1]) {
                this.trackBackgroundImageUrl(urlMatch[1], element);
                this.trackedElements.add(element);
                return;
            }
        }

        // If no inline style, check computed style (only when necessary)
        // Batch process with requestAnimationFrame to avoid forced synchronous layout
        this.pendingCheckElements.add(element);
        this.scheduleBackgroundImageCheck();
    }

    /**
     * Batch check pending background image elements
     */
    scheduleBackgroundImageCheck() {
        if (this.rafId !== null) {
            return; // Already scheduled
        }

        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            const elementsToCheck = Array.from(this.pendingCheckElements);
            this.pendingCheckElements.clear();

            elementsToCheck.forEach((element) => {
                if (!element.isConnected || this.trackedElements.has(element)) {
                    return;
                }

                const style = window.getComputedStyle(element);
                const backgroundImage = style.backgroundImage;

                if (backgroundImage && backgroundImage !== 'none') {
                    const urlMatch = backgroundImage.match(/url\(["']?([^"']+)["']?\)/);
                    if (urlMatch && urlMatch[1]) {
                        this.trackBackgroundImageUrl(urlMatch[1], element);
                        this.trackedElements.add(element);
                    }
                }
            });
        });
    }

    /**
     * Track a background image URL by creating a temporary Image object
     */
    trackBackgroundImageUrl(url: string, element: HTMLElement) {
        // Normalize URL (handle relative paths)
        const normalizedUrl = this.normalizeUrl(url);
        if (
            !normalizedUrl ||
            this.loadedImages.has(normalizedUrl) ||
            this.failedImages.has(normalizedUrl)
        ) {
            return;
        }

        // Create a temporary Image object to track loading
        const img = new Image();
        img.src = normalizedUrl;

        if (img.complete) {
            if (img.naturalHeight > 0) {
                this.loadedImages.add(normalizedUrl);
                this.onImageLoaded(normalizedUrl, element);
            } else {
                this.failedImages.add(normalizedUrl);
                this.onImageFailed(normalizedUrl, element);
            }
            return;
        }

        const loadHandler = () => {
            if (!this.loadedImages.has(normalizedUrl)) {
                this.loadedImages.add(normalizedUrl);
                this.onImageLoaded(normalizedUrl, element);
            }
        };

        const errorHandler = () => {
            if (!this.failedImages.has(normalizedUrl)) {
                this.failedImages.add(normalizedUrl);
                this.onImageFailed(normalizedUrl, element);
            }
        };

        img.addEventListener('load', loadHandler, { once: true });
        img.addEventListener('error', errorHandler, { once: true });
    }

    /**
     * Normalize URL and handle relative paths
     */
    normalizeUrl(url: string): string {
        if (!url) return '';
        // Remove quotes
        url = url.trim().replace(/^["']|["']$/g, '');
        // Convert relative paths to absolute paths
        if (url.startsWith('/') || url.startsWith('./') || !url.match(/^https?:\/\//)) {
            try {
                return new URL(url, window.location.href).href;
            } catch {
                return url;
            }
        }
        return url;
    }

    observeDOMChanges() {
        let pendingMutations: MutationRecord[] = [];
        let rafScheduled = false;

        const processMutations = () => {
            rafScheduled = false;
            const mutations = pendingMutations;
            pendingMutations = [];

            const addedImageNodes: HTMLImageElement[] = [];
            const addedElements = new Set<HTMLElement>();

            mutations.forEach((mutation) => {
                // Process added nodes
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;

                            if (element.tagName === 'IMG') {
                                addedImageNodes.push(element as HTMLImageElement);
                            } else {
                                // Only check directly added elements, don't traverse all descendants
                                addedElements.add(element);
                                // Only find direct child img elements, no recursion
                                const directImages = Array.from(element.children).filter(
                                    (child) => child.tagName === 'IMG'
                                ) as HTMLImageElement[];
                                addedImageNodes.push(...directImages);
                            }
                        }
                    });
                }

                // Handle style changes (only check the changed element itself)
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
                    const element = mutation.target;
                    if (
                        (mutation.attributeName === 'style' ||
                            mutation.attributeName === 'class') &&
                        !this.trackedElements.has(element)
                    ) {
                        addedElements.add(element);
                    }
                }
            });

            // Batch process
            if (addedImageNodes.length > 0 || addedElements.size > 0) {
                requestAnimationFrame(() => {
                    addedImageNodes.forEach((img) => {
                        if (img.isConnected && img.src) {
                            this.trackImage(img);
                            // Add to IntersectionObserver if not already tracked
                            if (this.intersectionObserver && !this.trackedElements.has(img)) {
                                this.intersectionObserver.observe(img);
                            }
                        }
                    });

                    // Only check directly added elements, don't recursively traverse all descendants
                    // Also add to IntersectionObserver for observation
                    addedElements.forEach((element) => {
                        if (element.isConnected) {
                            // Check inline styles first, track immediately if background image exists
                            this.checkAndTrackBackgroundImage(element);
                            // Add to IntersectionObserver for CSS background image detection
                            if (this.intersectionObserver && element.tagName !== 'IMG') {
                                this.intersectionObserver.observe(element);
                            }
                        }
                    });
                });
            }
        };

        this.observer = new MutationObserver((mutations) => {
            pendingMutations.push(...mutations);

            // Debounce: batch process multiple mutations
            if (!rafScheduled) {
                rafScheduled = true;
                requestAnimationFrame(processMutations);
            }
        });

        // Only observe necessary attribute changes
        this.observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });
    }

    /**
     * Setup IntersectionObserver to track images when they enter viewport
     * Primary method for checking background images, avoids full DOM traversal
     */
    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const element = entry.target as HTMLElement;

                        if (element.tagName === 'IMG') {
                            const img = element as HTMLImageElement;
                            if (img.src && !this.trackedElements.has(img)) {
                                this.trackImage(img);
                            }
                        } else {
                            // Only check background images when element enters viewport
                            this.checkAndTrackBackgroundImage(element);
                        }
                    }
                });
            },
            {
                root: null,
                rootMargin: '50px',
                threshold: 0.01,
            }
        );

        // Only observe existing <img> tags
        // Background image elements are dynamically added via MutationObserver
        document.querySelectorAll('img').forEach((img) => {
            this.intersectionObserver?.observe(img);
        });
    }

    // Clean up observers
    destroy() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }
        this.pendingCheckElements.clear();
    }

    trackImage(img: HTMLImageElement) {
        // Skip if image is not connected to DOM
        if (!img.isConnected) {
            return;
        }

        const src = img.src;
        if (!src) {
            return;
        }

        // Normalize URL
        const normalizedSrc = this.normalizeUrl(src);

        // Skip if already tracked
        if (this.loadedImages.has(normalizedSrc) || this.failedImages.has(normalizedSrc)) {
            return;
        }

        // Check if image is already loaded or failed
        if (img.complete) {
            if (img.naturalHeight > 0) {
                this.loadedImages.add(normalizedSrc);
                this.onImageLoaded(normalizedSrc, img);
            } else {
                this.failedImages.add(normalizedSrc);
                this.onImageFailed(normalizedSrc, img);
            }
            return;
        }

        // Track image load/error events
        // Use once option to avoid duplicate listeners
        const loadHandler = () => {
            if (!this.loadedImages.has(normalizedSrc)) {
                this.loadedImages.add(normalizedSrc);
                this.onImageLoaded(normalizedSrc, img);
            }
        };

        const errorHandler = () => {
            if (!this.failedImages.has(normalizedSrc)) {
                this.failedImages.add(normalizedSrc);
                this.onImageFailed(normalizedSrc, img);
            }
        };

        img.addEventListener('load', loadHandler, { once: true });
        img.addEventListener('error', errorHandler, { once: true });
    }

    onImageLoaded(src: string, _element: HTMLElement) {
        const isTrue = grayCalc(src);
        if (!isTrue) {
            return;
        }

        // track a log
        trackGA4EventAfterPageView<GA4.ImageURLInvalid>({
            event: 'image_url_invalid',
            imageURL: src,
        });
    }

    onImageFailed(src: string, _element: HTMLElement) {
        if (!isInHost(src)) {
            return;
        }
        trackGA4EventAfterPageView<GA4.ImageLoadFailed>({
            event: 'image_load_failed',
            imageURL: src,
        });
    }

    // Get statistics
    getStats() {
        return {
            total: this.loadedImages.size + this.failedImages.size,
            loaded: this.loadedImages.size,
            failed: this.failedImages.size,
            loadedUrls: Array.from(this.loadedImages),
            failedUrls: Array.from(this.failedImages),
        };
    }
}

export default ImageTracker;
